import { getCurrentOperatorName } from '../../utils/currentOperator';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import BaseTicketScreen from './BaseTicketScreen';
import { FormRow, FormGroup, Label, Input, Select, FieldError } from './shared/TicketStyledComponents';
import {
  SwapLegsContainer, SwapLegPanel, SwapLegHeader, SwapLegTitle, SwapLegIcon,
  SwapArrowDivider, SwapArrow, LegFormRow, LegFormRowSingle, SwapDirectionToggle, DirectionOption
} from './shared/SwapLegStyledComponents';
import { getTodayDate } from '../../utils/dateUtils';
import { calendarsFor } from '../../utils/businessDate';
import { provisionalSettleDate, resolveSettleDate, settlementCalendars, SettlementQuery } from '../../utils/settlement';
import { formatAmountWithCommas, parseAmountShorthand } from '../../utils/amountUtils';
import axios from 'axios';

import { DateField } from '../shared/DateField';

// ADR 0009 §4.2: a swap's dates run on its currency's calendar, not the book's. The ticket shows
// the settle date the server will store; the browser's count stands in until the server answers.
const settleQuery = (tradeDate: string, currency?: string): SettlementQuery =>
  ({ tradeDate, ticketType: 'IRS', convention: 'T+1', currency: currency || 'USD' });

/** Applies the server's settle date if the form still holds the inputs and the date it was asked about. */
const applyServerSettleDate = (setFormData: any, query: SettlementQuery, expected: string) => {
  resolveSettleDate(query).then(settleDate => setFormData((prev: any) =>
    prev.tradeDate === query.tradeDate && (prev.fixedCurrency || 'USD') === query.currency
      && prev.settleDate === expected && settleDate && settleDate !== expected
      ? { ...prev, settleDate } : prev));
};

/** After a trade date or currency change: the browser's count at once, then the server's. */
const refreshSettleDate = (setFormData: any, query: SettlementQuery) => {
  if (!query.tradeDate) return;
  const provisional = provisionalSettleDate(query);
  setFormData((prev: any) => ({ ...prev, settleDate: provisional }));
  applyServerSettleDate(setFormData, query, provisional);
};

const DerivativesIRSwapTicketScreen: React.FC = () => {
  const location = useLocation();

  const getPendingTradeData = () => {
    try {
      const pendingData = sessionStorage.getItem('pendingTradeData');
      if (pendingData) {
        const parsed = JSON.parse(pendingData);
        sessionStorage.removeItem('pendingTradeData');
        return parsed;
      }
    } catch (error) {
      console.error('Error reading pending trade data:', error);
    }
    return null;
  };

  const pendingTrade = getPendingTradeData();

  const getInitialFormData = () => {
    const hedgeData = location.state?.hedgeData;
    const portfolioId = location.state?.portfolioId;
    const portfolioName = location.state?.portfolioName;
    const tradeDate = pendingTrade?.tradeDate || getTodayDate(calendarsFor('IRS', pendingTrade?.fixedCurrency));

    const baseFormData = {
      // Direction: which leg the client receives
      direction: (pendingTrade?.direction || 'RECEIVE_FIXED') as string,
      assetManager: pendingTrade?.assetManager || '',
      portfolioManager: pendingTrade?.portfolioManager || '',
      assetClass: 'Derivatives',
      assetSubClass: 'Interest Rate Swaps',
      security: pendingTrade?.securityName || pendingTrade?.security || '',
      identifier: pendingTrade?.cusip || pendingTrade?.isin || pendingTrade?.identifier || '',
      // Common
      tradeDate,
      settleDate: pendingTrade?.settleDate || provisionalSettleDate(settleQuery(tradeDate, pendingTrade?.fixedCurrency)),
      effectiveDate: pendingTrade?.effectiveDate || '',
      maturityDate: pendingTrade?.maturityDate || '',
      tenor: pendingTrade?.tenor || '',
      portfolio: pendingTrade?.portfolioName || pendingTrade?.portfolio || '',
      submittedBy: pendingTrade?.submittedBy || getCurrentOperatorName(),
      executedBy: pendingTrade?.executedBy || '',
      notes: pendingTrade?.notes || '',
      // Fixed leg
      fixedCurrency: pendingTrade?.fixedCurrency || 'USD',
      fixedNotional: pendingTrade?.fixedNotional?.toString() || pendingTrade?.notionalAmount?.toString() || '',
      fixedRate: pendingTrade?.fixedRate?.toString() || '',
      fixedDayCount: pendingTrade?.fixedDayCount || '30/360',
      fixedPayFrequency: pendingTrade?.fixedPayFrequency || pendingTrade?.paymentFrequency || 'Semi-Annual',
      // Float leg
      floatCurrency: pendingTrade?.floatCurrency || 'USD',
      floatNotional: pendingTrade?.floatNotional?.toString() || pendingTrade?.notionalAmount?.toString() || '',
      floatIndex: pendingTrade?.floatIndex || pendingTrade?.floatingRateIndex || 'SOFR',
      floatSpread: pendingTrade?.floatSpread?.toString() || pendingTrade?.floatingRateSpread?.toString() || '',
      floatDayCount: pendingTrade?.floatDayCount || 'ACT/360',
      floatPayFrequency: pendingTrade?.floatPayFrequency || 'Quarterly',
      floatResetFrequency: pendingTrade?.floatResetFrequency || 'Quarterly',
    };

    if (hedgeData) {
      if (hedgeData.securityName) baseFormData.security = hedgeData.securityName;
      if (hedgeData.identifier) baseFormData.identifier = hedgeData.identifier;
      if (hedgeData.notionalAmount) {
        const notional = typeof hedgeData.notionalAmount === 'number'
          ? hedgeData.notionalAmount
          : parseFloat(hedgeData.notionalAmount);
        if (!isNaN(notional)) {
          const formatted = notional.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          baseFormData.fixedNotional = formatted;
          baseFormData.floatNotional = formatted;
        }
      }
      // F-HG-012/023: a duration hedge PAYS fixed — the ticket's receive-fixed
      // default is an anti-hedge for this intent. Honor the proposal's
      // direction (default pay-fixed when arriving from a hedge proposal),
      // and prefill tenor/dates from the reference so the swap is bookable
      // as proposed.
      baseFormData.direction = hedgeData.direction === 'RECEIVE_FIXED' ? 'RECEIVE_FIXED' : 'PAY_FIXED';
      if (hedgeData.tenor) baseFormData.tenor = String(hedgeData.tenor);
      if (hedgeData.referenceMaturityDate) baseFormData.maturityDate = String(hedgeData.referenceMaturityDate).slice(0, 10);
      if (!baseFormData.effectiveDate) baseFormData.effectiveDate = baseFormData.tradeDate;
      if (hedgeData.floatIndex) baseFormData.floatIndex = String(hedgeData.floatIndex);
      // Portfolio field is matched by NAME downstream — the numeric id never
      // resolved and silently dropped the proposal's portfolio.
      if (portfolioName) baseFormData.portfolio = String(portfolioName);
      else if (portfolioId) baseFormData.portfolio = String(portfolioId);
      // Provenance: keep the proposal trail on the ticket.
      const provenance = `Hedge proposal${hedgeData.identifier ? ` ${hedgeData.identifier}` : ''}${hedgeData.rationale ? `: ${hedgeData.rationale}` : ''}`;
      baseFormData.notes = baseFormData.notes ? `${baseFormData.notes}\n${provenance}` : provenance;
    }

    return baseFormData;
  };

  const initialFormData = getInitialFormData();

  const validateForm = (formData: any, action: string): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};
    if (!formData.assetManager?.trim()) errors.assetManager = 'Asset Manager is required';
    if (!formData.portfolioManager?.trim()) errors.portfolioManager = 'Portfolio Manager is required';
    if (!formData.fixedNotional?.trim()) errors.fixedNotional = 'Fixed Notional is required';
    if (!formData.fixedRate?.trim()) errors.fixedRate = 'Fixed Rate is required';
    if (!formData.floatIndex?.trim()) errors.floatIndex = 'Float Index is required';
    if (!formData.floatNotional?.trim()) errors.floatNotional = 'Float Notional is required';
    if (!formData.tenor?.trim() && !formData.maturityDate) errors.tenor = 'Tenor or Maturity Date is required';
    if (!formData.portfolio?.trim()) errors.portfolio = 'Portfolio is required';
    if (!formData.tradeDate) errors.tradeDate = 'Trade Date is required';
    if (!formData.settleDate) errors.settleDate = 'Settle Date is required';
    if (action === 'execute') {
      if (!formData.executedBy?.trim()) errors.executedBy = 'Executed By is required for execution';
      // NO fills gate: an OTC swap has no working order or partial fills —
      // execution is the bilateral confirmation of agreed terms. The backend
      // fills-mandatory block is correctly scoped to FUTURES/OPTIONS only.
    }
    return errors;
  };

  // IRS E2E rewire (2026-08-05): the ticket's terms ARE the bespoke contract —
  // /prepare mints dim_irs_contract + a classified per-contract SMF and returns
  // the sourceRef the posting SPI resolves at POST. The old path booked a bare,
  // contract-less trade against a hand-typed identifier and lost every swap term.
  const buildTransactionData = async (formData: any, dimensionData: any): Promise<any> => {
    const portfolio = dimensionData.portfolios.find((p: any) => p.portfolioName === formData.portfolio.trim());
    if (!portfolio) throw new Error(`Portfolio "${formData.portfolio}" not found`);

    const notional = parseAmountShorthand(formData.fixedNotional);
    const prepareRes = await axios.post('/api/derivatives/irs/prepare', {
      portfolioId: portfolio.id,
      direction: formData.direction === 'RECEIVE_FIXED' ? 'RECEIVE_FIXED' : 'PAY_FIXED',
      notional,
      currency: formData.fixedCurrency || 'USD',
      fixedRate: formData.fixedRate ? parseFloat(formData.fixedRate) : null,
      fixedDayCount: formData.fixedDayCount || null,
      fixedPaymentFreq: formData.fixedPayFrequency || null,
      floatIndex: formData.floatIndex || null,
      floatSpreadBps: formData.floatSpread ? parseFloat(formData.floatSpread) : null,
      floatResetFreq: formData.floatResetFrequency || null,
      floatPaymentFreq: formData.floatPayFrequency || null,
      floatDayCount: formData.floatDayCount || null,
      tradeDate: formData.tradeDate,
      effectiveDate: formData.effectiveDate || null,
      maturityDate: formData.maturityDate || null,
      tenor: formData.tenor || null,
    });
    const { securityId, sourceRef } = prepareRes.data;

    return {
      transactionType: formData.direction === 'RECEIVE_FIXED' ? 'BUY' : 'SELL',
      securityId,
      portfolioId: portfolio.id,
      // quantity = notional; price 100 is the par-style convention for the
      // fill/execute flow. amount = notional (blotter $); netAmount = 0 — an
      // at-market swap moves NO cash at inception (the IRS posting SPI books
      // MV = NPV ~ 0, cost 0, zero cash).
      quantity: notional,
      price: 100,
      amount: notional,
      netAmount: 0,
      tradeDate: formData.tradeDate,
      settleDate: formData.settleDate,
      notes: formData.notes || `IRS ${formData.direction === 'RECEIVE_FIXED' ? 'Receive' : 'Pay'} Fixed ${formData.fixedRate}% vs ${formData.floatIndex} ${formData.tenor || ''}`,
      createdBy: formData.submittedBy,
      executedBy: formData.executedBy || null,
      status: 'DRAFT',
      ticketType: 'IRS',
      eventType: 'ACQUIRE',
      sourceRef,
    };
  };

  // Reopen/edit: swap terms live on dim_irs_contract (resolved by sourceRef),
  // not as flat trade fields.
  const loadTransactionData = async (transaction: any) => {
    const tradeDate = transaction.tradeDate || getTodayDate(calendarsFor('IRS', transaction.currency));
    const base = {
      direction: transaction.transactionType === 'SELL' ? 'PAY_FIXED' : 'RECEIVE_FIXED',
      assetManager: transaction.assetManagerName || transaction.assetManager || '',
      portfolioManager: transaction.portfolioManagerName || transaction.portfolioManager || '',
      assetClass: 'Derivatives',
      assetSubClass: 'Interest Rate Swaps',
      security: transaction.securityName || transaction.security || '',
      identifier: '',
      tradeDate,
      settleDate: transaction.settleDate || provisionalSettleDate(settleQuery(tradeDate, transaction.currency)),
      effectiveDate: '',
      maturityDate: '',
      tenor: '',
      portfolio: transaction.portfolioName || transaction.portfolio || '',
      submittedBy: transaction.createdBy || getCurrentOperatorName(),
      executedBy: transaction.executedBy || '',
      notes: transaction.notes || '',
      fixedCurrency: transaction.currency || 'USD',
      fixedNotional: transaction.amount?.toString() || '',
      fixedRate: '',
      fixedDayCount: '30/360',
      fixedPayFrequency: 'Semi-Annual',
      floatCurrency: transaction.currency || 'USD',
      floatNotional: transaction.amount?.toString() || '',
      floatIndex: 'SOFR',
      floatSpread: '',
      floatDayCount: 'ACT/360',
      floatPayFrequency: 'Quarterly',
      floatResetFrequency: 'Quarterly',
    };
    const m = (transaction.sourceRef || '').match(/irs-contract:(\d+)/);
    if (!m) return base;
    try {
      const c = (await axios.get(`/api/derivatives/irs/contract/${m[1]}`)).data;
      return {
        ...base,
        direction: c.fixedPayerReceiver === 'PAYER' ? 'PAY_FIXED' : 'RECEIVE_FIXED',
        effectiveDate: c.effectiveDate || '',
        maturityDate: c.maturityDate || '',
        fixedCurrency: c.notionalCurrency || base.fixedCurrency,
        floatCurrency: c.notionalCurrency || base.floatCurrency,
        fixedNotional: c.notionalAmount != null ? String(c.notionalAmount) : base.fixedNotional,
        floatNotional: c.notionalAmount != null ? String(c.notionalAmount) : base.floatNotional,
        fixedRate: c.fixedRate != null ? String(c.fixedRate) : '',
        fixedDayCount: c.fixedDayCount || base.fixedDayCount,
        fixedPayFrequency: c.fixedPaymentFreq || base.fixedPayFrequency,
        floatIndex: c.floatingIndex || base.floatIndex,
        floatSpread: c.floatingSpreadBps != null ? String(c.floatingSpreadBps) : '',
        floatDayCount: c.floatingDayCount || base.floatDayCount,
        floatPayFrequency: c.floatingPaymentFreq || base.floatPayFrequency,
        floatResetFrequency: c.floatingResetFreq || base.floatResetFrequency,
      };
    } catch { return base; }
  };

  return (
    <BaseTicketScreen
      title="Interest Rate Swap Trade Entry"
      assetClass="Derivatives"
      assetSubClass="Interest Rate Swaps"
      initialFormData={initialFormData}
      validateForm={validateForm}
      buildTransactionData={buildTransactionData}
      loadTransactionData={loadTransactionData}
    >
      {({ formData, setFormData, errors, dimensionData, transactionStatus, isFieldDisabled, handleInputChange, handleInputBlur, editingTransactionId }) => (
        <IRSwapFormContent
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          dimensionData={dimensionData}
          transactionStatus={transactionStatus}
          isFieldDisabled={isFieldDisabled}
          handleInputChange={handleInputChange}
          handleInputBlur={handleInputBlur}
          location={location}
          editingTransactionId={editingTransactionId}
        />
      )}
    </BaseTicketScreen>
  );
};

const IRSwapFormContent: React.FC<{
  formData: any;
  setFormData: any;
  errors: any;
  dimensionData: any;
  transactionStatus: any;
  isFieldDisabled: any;
  handleInputChange: any;
  handleInputBlur: any;
  location: any;
  editingTransactionId: number | null;
}> = ({ formData, setFormData, errors, dimensionData, transactionStatus, isFieldDisabled, handleInputChange, handleInputBlur, location, editingTransactionId }) => {

  useEffect(() => {
    const portfolioId = location.state?.portfolioId;
    const portfolioName = location.state?.portfolioName;
    const currentPortfolio = formData.portfolio;
    const isPortfolioId = currentPortfolio && /^\d+$/.test(String(currentPortfolio).trim());
    if (portfolioName && (!currentPortfolio || isPortfolioId)) {
      setFormData((prev: any) => ({ ...prev, portfolio: portfolioName }));
    } else if (portfolioId && dimensionData.portfolios.length > 0 && (!currentPortfolio || isPortfolioId)) {
      const portfolio = dimensionData.portfolios.find((p: any) => String(p.id) === String(portfolioId));
      if (portfolio?.portfolioName) {
        setFormData((prev: any) => ({ ...prev, portfolio: portfolio.portfolioName }));
      }
    }
  }, [dimensionData.portfolios, location.state?.portfolioId, location.state?.portfolioName, formData.portfolio, setFormData]);

  // A new ticket asks the server for its settle date on open; a reopened trade keeps its own.
  useEffect(() => {
    if (editingTransactionId || location.state?.transactionId || location.state?.id || !formData.tradeDate) return;
    applyServerSettleDate(setFormData, settleQuery(formData.tradeDate, formData.fixedCurrency), formData.settleDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync notional across legs for vanilla IRS
  const handleNotionalChange = (leg: 'fixed' | 'float', value: string) => {
    const fixedKey = leg === 'fixed' ? 'fixedNotional' : 'floatNotional';
    const otherKey = leg === 'fixed' ? 'floatNotional' : 'fixedNotional';
    setFormData((prev: any) => ({ ...prev, [fixedKey]: value, [otherKey]: value }));
  };

  const handleNotionalBlur = (value: string) => {
    const parsed = parseAmountShorthand(value);
    if (parsed > 0) {
      const formatted = formatAmountWithCommas(parsed);
      setFormData((prev: any) => ({ ...prev, fixedNotional: formatted, floatNotional: formatted }));
    }
  };

  const isReceiveFixed = formData.direction === 'RECEIVE_FIXED';

  return (
    <>
      {/* ── Header Row: Direction, Trade Date, Settle Date ── */}
      <FormRow>
        <FormGroup>
          <Label>Direction*</Label>
          <SwapDirectionToggle>
            <DirectionOption
              variant="receive"
              active={isReceiveFixed}
              disabled={isFieldDisabled('direction')}
              onClick={() => handleInputChange('direction', 'RECEIVE_FIXED')}
            >
              Receive Fixed / Pay Float
            </DirectionOption>
            <DirectionOption
              variant="pay"
              active={!isReceiveFixed}
              disabled={isFieldDisabled('direction')}
              onClick={() => handleInputChange('direction', 'PAY_FIXED')}
            >
              Pay Fixed / Receive Float
            </DirectionOption>
          </SwapDirectionToggle>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="tradeDate">Trade Date*</Label>
          <DateField
            id="tradeDate"
            value={formData.tradeDate ?? ''}
            className={errors.tradeDate ? 'error' : ''}
            disabled={isFieldDisabled('tradeDate')}
            calendars={calendarsFor('IRS', formData.fixedCurrency)}
            onChange={iso => {
              handleInputChange('tradeDate', iso);
              handleInputBlur('tradeDate', iso);
              refreshSettleDate(setFormData, settleQuery(iso, formData.fixedCurrency));
            }}
          />
          {errors.tradeDate && <FieldError>{errors.tradeDate}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="settleDate">Settle Date*</Label>
          <DateField
            id="settleDate"
            value={formData.settleDate ?? ''}
            className={errors.settleDate ? 'error' : ''}
            disabled={isFieldDisabled('settleDate')}
            calendars={settlementCalendars({ ticketType: 'IRS', currency: formData.fixedCurrency })}
            onChange={iso => handleInputChange('settleDate', iso)}
          />
          {errors.settleDate && <FieldError>{errors.settleDate}</FieldError>}
        </FormGroup>
      </FormRow>

      {/* ── Row 2: Asset Manager, PM, Executed By ── */}
      <FormRow>
        <FormGroup>
          <Label htmlFor="assetManager">Asset Manager*</Label>
          <Select
            id="assetManager"
            value={formData.assetManager}
            onChange={(e) => handleInputChange('assetManager', e.target.value)}
            className={errors.assetManager ? 'error' : ''}
            disabled={isFieldDisabled('assetManager')}
          >
            <option value="">Select Asset Manager</option>
            {dimensionData.assetManagers.map((am: any) => (
              <option key={am.id} value={am.assetManagerName}>{am.assetManagerName}</option>
            ))}
          </Select>
          {errors.assetManager && <FieldError>{errors.assetManager}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="portfolioManager">Portfolio Manager*</Label>
          <Select
            id="portfolioManager"
            value={formData.portfolioManager}
            onChange={(e) => handleInputChange('portfolioManager', e.target.value)}
            className={errors.portfolioManager ? 'error' : ''}
            disabled={isFieldDisabled('portfolioManager')}
          >
            <option value="">Select Portfolio Manager</option>
            {dimensionData.portfolioManagers.map((pm: any) => (
              <option key={pm.id} value={pm.portfolioManagerName}>{pm.portfolioManagerName}</option>
            ))}
          </Select>
          {errors.portfolioManager && <FieldError>{errors.portfolioManager}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="executedBy">Executed By{transactionStatus === 'SENT' ? '*' : ''}</Label>
          <Select
            id="executedBy"
            value={formData.executedBy}
            onChange={(e) => handleInputChange('executedBy', e.target.value)}
            disabled={isFieldDisabled('executedBy')}
            className={errors.executedBy ? 'error' : ''}
          >
            <option value="">Select</option>
            {dimensionData.users.map((user: any) => (
              <option key={user.id} value={user.fullName}>{user.fullName} {user.role ? `(${user.role})` : ''}</option>
            ))}
          </Select>
          {errors.executedBy && <FieldError>{errors.executedBy}</FieldError>}
        </FormGroup>
      </FormRow>

      {/* ── Row 3: Contract Name, Identifier, Tenor ── */}
      <FormRow>
        <FormGroup>
          <Label htmlFor="security">Contract Name</Label>
          <Input
            id="security"
            type="text"
            value={formData.security}
            onChange={(e) => handleInputChange('security', e.target.value)}
            placeholder="e.g., USD 5Y IRS"
            disabled={isFieldDisabled('security')}
          />
        </FormGroup>

        {/* Identifier removed (IRS E2E 2026-08-05): the swap is bespoke — the
            contract + its IRS{id} security are minted from these terms at
            draft save; there is no pre-existing identifier to type. */}

        <FormGroup>
          <Label htmlFor="tenor">Tenor*</Label>
          <Input
            id="tenor"
            type="text"
            value={formData.tenor}
            onChange={(e) => handleInputChange('tenor', e.target.value)}
            placeholder="e.g., 5Y, 10Y"
            className={errors.tenor ? 'error' : ''}
            disabled={isFieldDisabled('tenor')}
          />
          {errors.tenor && <FieldError>{errors.tenor}</FieldError>}
        </FormGroup>
      </FormRow>

      {/* ── Row 4: Effective Date, Maturity Date ── */}
      <FormRow>
        <FormGroup>
          <Label htmlFor="effectiveDate">Effective Date</Label>
          <DateField
            id="effectiveDate"
            value={formData.effectiveDate ?? ''}
            disabled={isFieldDisabled('effectiveDate')}
            onChange={iso => handleInputChange('effectiveDate', iso)}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="maturityDate">Maturity Date</Label>
          <DateField
            id="maturityDate"
            value={formData.maturityDate ?? ''}
            disabled={isFieldDisabled('maturityDate')}
            onChange={iso => handleInputChange('maturityDate', iso)}
          />
        </FormGroup>

        <FormGroup />
      </FormRow>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/*  TWO-PANEL SWAP LEGS                                               */}
      {/* ════════════════════════════════════════════════════════════════════ */}

      <SwapLegsContainer>
        {/* ── FIXED LEG ── */}
        <SwapLegPanel variant={isReceiveFixed ? 'receive' : 'pay'}>
          <SwapLegHeader variant={isReceiveFixed ? 'receive' : 'pay'}>
            <SwapLegIcon variant={isReceiveFixed ? 'receive' : 'pay'}>
              {isReceiveFixed ? '↓' : '↑'}
            </SwapLegIcon>
            <SwapLegTitle variant={isReceiveFixed ? 'receive' : 'pay'}>
              {isReceiveFixed ? 'Receive' : 'Pay'} Fixed
            </SwapLegTitle>
          </SwapLegHeader>

          <LegFormRow>
            <FormGroup>
              <Label htmlFor="fixedCurrency">Currency*</Label>
              <Select
                id="fixedCurrency"
                value={formData.fixedCurrency}
                onChange={(e) => {
                  handleInputChange('fixedCurrency', e.target.value);
                  refreshSettleDate(setFormData, settleQuery(formData.tradeDate, e.target.value));
                }}
                disabled={isFieldDisabled('fixedCurrency')}
              >
                {dimensionData.currencies.map((c: any) => (
                  <option key={c.id} value={c.currencyCode}>{c.currencyCode}</option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="fixedNotional">Notional Amount*</Label>
              <Input
                id="fixedNotional"
                type="text"
                value={formData.fixedNotional}
                onChange={(e) => handleNotionalChange('fixed', e.target.value)}
                onBlur={(e) => handleNotionalBlur(e.target.value)}
                placeholder="e.g., 50MM"
                className={errors.fixedNotional ? 'error' : ''}
                disabled={isFieldDisabled('fixedNotional')}
              />
              {errors.fixedNotional && <FieldError>{errors.fixedNotional}</FieldError>}
            </FormGroup>
          </LegFormRow>

          <LegFormRowSingle>
            <FormGroup>
              <Label htmlFor="fixedRate">Fixed Rate (%)*</Label>
              <Input
                id="fixedRate"
                type="number"
                step="0.0001"
                value={formData.fixedRate}
                onChange={(e) => handleInputChange('fixedRate', e.target.value)}
                placeholder="e.g., 4.2500"
                className={errors.fixedRate ? 'error' : ''}
                disabled={isFieldDisabled('fixedRate')}
              />
              {errors.fixedRate && <FieldError>{errors.fixedRate}</FieldError>}
            </FormGroup>
          </LegFormRowSingle>

          <LegFormRow>
            <FormGroup>
              <Label htmlFor="fixedDayCount">Day Count</Label>
              <Select
                id="fixedDayCount"
                value={formData.fixedDayCount}
                onChange={(e) => handleInputChange('fixedDayCount', e.target.value)}
                disabled={isFieldDisabled('fixedDayCount')}
              >
                <option value="30/360">30/360</option>
                <option value="ACT/360">ACT/360</option>
                <option value="ACT/365">ACT/365</option>
                <option value="ACT/ACT">ACT/ACT</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="fixedPayFrequency">Payment Frequency</Label>
              <Select
                id="fixedPayFrequency"
                value={formData.fixedPayFrequency}
                onChange={(e) => handleInputChange('fixedPayFrequency', e.target.value)}
                disabled={isFieldDisabled('fixedPayFrequency')}
              >
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Semi-Annual">Semi-Annual</option>
                <option value="Annual">Annual</option>
              </Select>
            </FormGroup>
          </LegFormRow>
        </SwapLegPanel>

        {/* ── ARROW DIVIDER ── */}
        <SwapArrowDivider>
          <SwapArrow>⇄</SwapArrow>
        </SwapArrowDivider>

        {/* ── FLOAT LEG ── */}
        <SwapLegPanel variant={isReceiveFixed ? 'pay' : 'receive'}>
          <SwapLegHeader variant={isReceiveFixed ? 'pay' : 'receive'}>
            <SwapLegIcon variant={isReceiveFixed ? 'pay' : 'receive'}>
              {isReceiveFixed ? '↑' : '↓'}
            </SwapLegIcon>
            <SwapLegTitle variant={isReceiveFixed ? 'pay' : 'receive'}>
              {isReceiveFixed ? 'Pay' : 'Receive'} Float
            </SwapLegTitle>
          </SwapLegHeader>

          <LegFormRow>
            <FormGroup>
              <Label htmlFor="floatCurrency">Currency*</Label>
              <Select
                id="floatCurrency"
                value={formData.floatCurrency}
                onChange={(e) => handleInputChange('floatCurrency', e.target.value)}
                disabled={isFieldDisabled('floatCurrency')}
              >
                {dimensionData.currencies.map((c: any) => (
                  <option key={c.id} value={c.currencyCode}>{c.currencyCode}</option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="floatNotional">Notional Amount*</Label>
              <Input
                id="floatNotional"
                type="text"
                value={formData.floatNotional}
                onChange={(e) => handleNotionalChange('float', e.target.value)}
                onBlur={(e) => handleNotionalBlur(e.target.value)}
                placeholder="e.g., 50MM"
                className={errors.floatNotional ? 'error' : ''}
                disabled={isFieldDisabled('floatNotional')}
              />
              {errors.floatNotional && <FieldError>{errors.floatNotional}</FieldError>}
            </FormGroup>
          </LegFormRow>

          <LegFormRow>
            <FormGroup>
              <Label htmlFor="floatIndex">Rate Index*</Label>
              <Select
                id="floatIndex"
                value={formData.floatIndex}
                onChange={(e) => handleInputChange('floatIndex', e.target.value)}
                className={errors.floatIndex ? 'error' : ''}
                disabled={isFieldDisabled('floatIndex')}
              >
                <option value="">Select</option>
                <option value="SOFR">SOFR</option>
                <option value="EURIBOR">EURIBOR</option>
                <option value="SONIA">SONIA</option>
                <option value="TONAR">TONAR</option>
              </Select>
              {errors.floatIndex && <FieldError>{errors.floatIndex}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="floatSpread">Spread (bps)</Label>
              <Input
                id="floatSpread"
                type="number"
                step="0.01"
                value={formData.floatSpread}
                onChange={(e) => handleInputChange('floatSpread', e.target.value)}
                placeholder="e.g., 25.00"
                disabled={isFieldDisabled('floatSpread')}
              />
            </FormGroup>
          </LegFormRow>

          <LegFormRow>
            <FormGroup>
              <Label htmlFor="floatDayCount">Day Count</Label>
              <Select
                id="floatDayCount"
                value={formData.floatDayCount}
                onChange={(e) => handleInputChange('floatDayCount', e.target.value)}
                disabled={isFieldDisabled('floatDayCount')}
              >
                <option value="ACT/360">ACT/360</option>
                <option value="ACT/365">ACT/365</option>
                <option value="ACT/ACT">ACT/ACT</option>
                <option value="30/360">30/360</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="floatPayFrequency">Payment Frequency</Label>
              <Select
                id="floatPayFrequency"
                value={formData.floatPayFrequency}
                onChange={(e) => handleInputChange('floatPayFrequency', e.target.value)}
                disabled={isFieldDisabled('floatPayFrequency')}
              >
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Semi-Annual">Semi-Annual</option>
                <option value="Annual">Annual</option>
              </Select>
            </FormGroup>
          </LegFormRow>

          <LegFormRowSingle>
            <FormGroup>
              <Label htmlFor="floatResetFrequency">Reset Frequency</Label>
              <Select
                id="floatResetFrequency"
                value={formData.floatResetFrequency}
                onChange={(e) => handleInputChange('floatResetFrequency', e.target.value)}
                disabled={isFieldDisabled('floatResetFrequency')}
              >
                <option value="Daily">Daily</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Semi-Annual">Semi-Annual</option>
              </Select>
            </FormGroup>
          </LegFormRowSingle>
        </SwapLegPanel>
      </SwapLegsContainer>
    </>
  );
};

export default DerivativesIRSwapTicketScreen;
